"use client";

import { useRouter } from 'next/navigation'
import { useSession, signIn, signOut } from 'next-auth/react';
import Image from "next/image"
import { ChevronDown, User } from "lucide-react"

import { usePathname } from 'next/navigation';
import { useCompetitionContext } from "../providers/CompetitionContext";
import { getFantasyRoute } from "../lib/getFantasyRoute";
import { useTranslation } from "../hooks/useTranslation";

export function SignInButton() {
  const { data: session, status } = useSession();
  const router = useRouter();

  if (status === 'loading') {
    return <>...</>;
  }

  if (status === 'authenticated') {
    return(
      <button 
        onClick={() => signOut({ callbackUrl: "/" })} 
        className="bg-[#FDC300] text-[#000000] text-xs sm:text-sm hover:bg-yellow-300 font-bold py-1.5 px-2.5 sm:py-2 sm:px-4 rounded-full flex items-center"
      >
        <User className="mr-1 sm:mr-2 h-4 w-4 sm:h-5 sm:w-5" />
        <span className="hidden xs:inline sm:inline">Déconnexion</span>
        <span className="xs:hidden sm:hidden">Déconnexion</span>
      </button>
    )
  }

  return (
    <button 
      onClick={() =>
        signIn('rtbf', {
          callbackUrl: '/onboarding/username?next=/fantasy',
        })
      }
      className="bg-[#FDC300] text-[#000000] text-xs sm:text-sm hover:bg-yellow-300 font-bold py-1.5 px-2.5 sm:py-2 sm:px-4 rounded-full flex items-center"
    >
      <User className="mr-1 sm:mr-2 h-4 w-4 sm:h-5 sm:w-5" />
      <span className="hidden xs:inline sm:inline">Connexion</span>
      <span className="xs:hidden sm:hidden">Connexion</span>
    </button>
  );
}

export function PlayButton() {
  const { data: session, status } = useSession();
  const router = useRouter();
  const { competitionData } = useCompetitionContext();
  const teamId = (session?.user as any)?.teamId as string | undefined;

  if (status === 'loading') {
    return <>...</>;
  }

  if (status === 'authenticated' && !teamId) {
    return (
      <button
        onClick={() => router.push("/fantasy/new")}
        className="bg-[#FDC300] text-[#000000] hover:bg-yellow-300 font-bold py-2 px-3 sm:py-3 sm:px-6 rounded-full flex items-center text-sm sm:text-base"
      >
        <span className="hidden sm:inline">Construis ton équipe</span>
        <span className="sm:hidden">Nouvelle équipe</span>
      </button>
    );
  }

  if (status === 'authenticated' && teamId) {
    const route = getFantasyRoute(
      teamId,
      competitionData?.deadlineInfo,
      competitionData?.matches?.length > 0
    );
    return (
      <button
        onClick={() => router.push(route)}
        className="bg-[#FDC300] text-[#000000] hover:bg-yellow-300 font-bold py-2 px-3 sm:py-3 sm:px-6 rounded-full flex items-center text-sm sm:text-base"
      >
        <span className="hidden sm:inline">Vers ton équipe</span>
        <span className="sm:hidden">Mon équipe</span>
      </button>
    );
  }
  
  return (
    <button 
      onClick={() =>
        signIn('rtbf', {
          callbackUrl: '/onboarding/username?next=/fantasy',
        })
      }
      className="bg-[#FDC300] text-[#000000] hover:bg-yellow-300 font-bold py-2 px-3 sm:py-3 sm:px-6 rounded-full flex items-center text-sm sm:text-base"
    >
      <User className="mr-1 sm:mr-2 h-4 w-4 sm:h-5 sm:w-5" />
      <span className="hidden sm:inline">Connexion</span>
      <span className="sm:hidden">Connexion</span>
    </button>
  );
}

import { useState } from "react"
import Link from "next/link"

export default function SubMenu() {
  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false)
  const subMenuId = "submenu-content"
  const { t } = useTranslation();

  const { data: session, status } = useSession({
    required: false,
    // refetchInterval: 0,
    // revalidateOnFocus: false,
    // revalidateOnReconnect: false
  });

  // const toggleMenu = () => {
  //   setIsMenuOpen(!isMenuOpen)
  // }

  const pathname = usePathname();
  const teamId = (session?.user as any)?.teamId as string | undefined;
  const isAuthenticated =
    status === "authenticated" && !!(session?.user?.dbUserId ?? session?.user?.id);

  let navigation = [
    { name: 'Accueil', href: '/', current: false },
    { name: 'Prono', href: '/fantasy/predictions', current: false },
    { name: t('menu.prizes'), href: '/prizes', current: false },
    { name: 'Règlement', href: '/rules', current: false },
  ].map(item => ({
    ...item,
    current: pathname === item.href
  }));

  // Dynamic navigation based on authentication and team status
  if (isAuthenticated) {
    navigation.splice(2, 0, {
      name: 'Ligues',
      href: '/fantasy/leagues',
      current: pathname === '/fantasy/leagues'
    });

    navigation.splice(3, 0, {
      name: t('menu.rankings'),
      href: '/fantasy/rankings',
      current: pathname === '/fantasy/rankings' || pathname.startsWith('/fantasy/rankings/')
    });

    if (teamId) {
      navigation.splice(4, 0, { 
        name: 'Equipe', 
        href: `/fantasy/edit/${teamId}`,
        current: (pathname === `/fantasy/edit/${teamId}`) || (pathname === `/fantasy/points/${teamId}`)
      });
    }
  }

  return (
    <aside
      id="sous-menu"
      aria-label="Sous menu Sport"
      className="bg-pureblue text-white dark:bg-sky-800 dark:text-sky-100 z-30 w-full"
    >
      <div className="mx-auto relative flex w-full max-w-6xl flex-col items-stretch px-3 py-2 sm:px-4 xl:px-0 md:flex-row md:items-center md:py-2.5">
        {/* Mobile header row */}
        <div className="flex shrink-0 justify-between items-center py-1 md:hidden">
          <span className="text-sm font-bold tracking-wide">Bonjour {session?.user?.username}</span>
          <button
            id="submenu-button"
            aria-controls={subMenuId}
            aria-expanded={isSubMenuOpen}
            onClick={() => setIsSubMenuOpen(!isSubMenuOpen)}
            className="flex items-center rounded-full border border-white/40 bg-white/10 px-3 py-1 text-sm font-semibold text-white outline-none transition-colors hover:bg-white/20 focus:ring-2 focus:ring-white/50"
          >
            Navigation
            <ChevronDown
              className={`ml-1.5 h-4 w-4 text-white transition-transform duration-200 ${isSubMenuOpen ? "rotate-180" : ""}`}
              aria-hidden="true"
            />
          </button>
        </div>

        {/* Navigation pills */}
        <nav className="flex-auto">
          <ul
            id={subMenuId}
            className={`flex flex-col gap-1.5 py-2 md:flex-row md:items-center md:gap-1.5 md:py-0 ${isSubMenuOpen ? "block" : "hidden md:flex"}`}
          >
            {navigation.map((item, index) => (
              <li key={index}>
                <a
                  className={`block rounded-full px-4 py-1.5 text-sm font-semibold transition-all duration-200 md:inline-block md:whitespace-nowrap
                    ${item.current
                      ? 'bg-[#FDC300] text-[#003d60] shadow-sm'
                      : 'text-white hover:bg-white/15 active:bg-white/25'
                    }`}
                  href={item.href}
                >
                  {item.name}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {/* Right-side logos */}
        <div className="hidden md:flex shrink-0 items-center gap-x-5 ml-4">
          <Link
            aria-label="RTBF Actus - Accueil"
            className="py-1 outline-none focus:ring-2 focus:ring-offset-2 focus:outline-white/70"
            data-elb="navigation"
            data-elbcontext="area:footer;label_area:Accueil"
            data-elbaction="click:click"
            href="https://www.rtbf.be"
          >
            <Image
              src="/img/RTBFActus.svg"
              alt="RTBF Actus Logo"
              className="inline-block h-8 filter invert"
              width={120}
              height={32}
            />
          </Link>
          <Link
            className="py-1 outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white/70 focus:ring-offset-slate-800"
            aria-label="RTBF Auvio"
            href="https://auvio.rtbf.be/"
          >
            <span className="sr-only">RTBF Auvio</span>
            <Image
              src="https://static-oaos.rtbf.be/icons/custom/brands/black/Auvio.svg"
              alt="RTBF Auvio Logo"
              className="inline-block h-8 filter invert"
              width={115}
              height={32}
            />
          </Link>
        </div>
      </div>
    </aside>
  )
}
